/**
 * Where-is-my-data page. Explicit, single-page answer to "where does SFP keep
 * everything it ingests and produces". Mirrors firestore.rules + ingest pipeline
 * so the user can audit it without reading code.
 */
export function StorageInfo() {
  const project = (import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined) ?? 'shulkin-family-partners';
  const bucket = (import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string | undefined) ?? `${project}.firebasestorage.app`;

  return (
    <div className="mx-auto max-w-3xl px-6 py-8 space-y-6">
      <header>
        <div className="text-[10px] uppercase tracking-wider text-stone-400">Reference</div>
        <h1 className="text-2xl font-semibold text-stone-800">Where is my data stored?</h1>
        <p className="mt-1 text-sm text-stone-600">
          Every byte SFP keeps — files, metadata, embeddings, tokens, settings — lives in one of these
          Google Cloud locations, scoped to the <code className="font-mono">{project}</code> project.
        </p>
      </header>

      <Section title="Raw files" id="storage">
        <Row label="Cloud Storage bucket" value={`gs://${bucket}`} />
        <Row label="Uploaded documents" value={`gs://${bucket}/uploads/{entityId}/{docId}/{filename}`} />
        <Row label="Documents crawled from Dropbox" value={`gs://${bucket}/uploads/dropbox/{uid}/{docId}/{filename}`} />
        <Row label="Extracted text (from PDFs / OCR)" value={`gs://${bucket}/text/{docId}.txt`} />
        <Note>
          Files are encrypted at rest by Google. Access is gated by{' '}
          <code className="font-mono">storage.rules</code> — owner + role-allowed only.
        </Note>
      </Section>

      <Section title="Structured metadata (Firestore)">
        <Row label="Document records" value="documents/{docId}" />
        <Row label="Entity master" value="entities/{entityId}" />
        <Row label="Bills + linked accounts" value="bills/{billId}, accounts/{accountId}" />
        <Row label="Users + roles" value="users/{uid}" />
        <Row label="Email allowlist" value="allowlist/{email}" />
        <Row label="Audit log" value="audit/{auditId}" />
        <Row label="Per-user home layout" value="homeLayouts/{uid}" />
        <Row label="Per-user scratchpad notes" value="userNotes/{uid}" />
        <Row label="Integration tokens (Dropbox refresh, etc.)" value="integrations/{uid}" />
      </Section>

      <Section title="Search index">
        <Row label="Voyage embeddings" value="documents/{docId}.embedding (1024-dim vector)" />
        <Row label="Firestore vector index" value="firestore.indexes.json — built async on first deploy" />
      </Section>

      <Section title="Secrets (never in the codebase)">
        <Row label="Anthropic API key" value="Google Secret Manager — ANTHROPIC_API_KEY" />
        <Row label="Voyage API key" value="Google Secret Manager — VOYAGE_API_KEY" />
        <Row label="Dropbox app credentials" value="Google Secret Manager — DROPBOX_APP_KEY, DROPBOX_APP_SECRET" />
      </Section>

      <Section title="How to leave / extract everything">
        <ul className="list-disc space-y-1 pl-5 text-xs text-stone-600">
          <li>Use the master .xlsx export (Documents → Master Export) to pull all structured metadata.</li>
          <li>
            Cloud Storage objects can be copied wholesale with{' '}
            <code className="font-mono">gcloud storage cp -r gs://{bucket} ./local-backup</code>.
          </li>
          <li>
            Firestore can be exported with{' '}
            <code className="font-mono">gcloud firestore export gs://{bucket}/firestore-backup</code>.
          </li>
        </ul>
      </Section>
    </div>
  );
}

function Section({ title, id, children }: { title: string; id?: string; children: React.ReactNode }) {
  return (
    <section id={id} className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-stone-800">{title}</h2>
      <div className="mt-3 space-y-1.5">{children}</div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[180px_1fr] gap-3 text-xs">
      <div className="text-stone-500">{label}</div>
      <div className="font-mono text-stone-800">{value}</div>
    </div>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-2 rounded bg-stone-50 px-3 py-2 text-[11px] text-stone-600">{children}</div>
  );
}
